import {
  Form,
  Row,
  Col,
  Input,
  Button,
  Select,
  DatePicker,
  Switch,
  InputNumber,
  message,
  Tooltip,
  Divider,
  Space,
  InputRef,
  Drawer,
  Checkbox,
  Grid,
  Card,
  Modal,
} from "antd";
import React, { useContext, useEffect, useRef, useState } from "react";
import {
  ACTIVITY_INITIAL_STATE,
  PROJECT_INITIAL_STATE,
  TASK_INITIAL_STATE,
} from "../../utils/data";
import {
  ProjectProps,
  ProjectTemplateProps,
} from "../../interfaces/interfaces";
import { CustomerListWithNoDetails } from "../customer";
import dayjs from "dayjs";
import DataContext from "../../context/DataContext";
import AuthContext from "../../context/AuthContext";
import { PlusOutlined, UsergroupAddOutlined } from "@ant-design/icons";
import { TagSelector } from "../ui";
import { filterOption, filterOptionGroup } from "../../utils/func/utils";
import { TaskList } from "../task";
import { ActivityList } from "../activity";

interface Props {
  modalStatus: {
    open: boolean;
    create: boolean;
    update: boolean;
    selected?: string[];
    payload?: ProjectProps | null;
  } | null;
  idClient: string;
  handleCreateLocation: (locationName: string) => void;
  handleCreateNewProjectStatus: (projectStatusName: string) => void;
  handleCreateSource: (sourceName: string) => void;
  handleChangeModalStatus: (payload?: ProjectProps) => void;
  onCancel: () => void;
  onSubmit: (values: ProjectProps) => void;
}

const fieldTypes = [
  { value: "String", label: "String" },
  { value: "Number", label: "Number" },
  { value: "Date", label: "Date" },
  { value: "Boolean", label: "Boolean" },
];

export const DrawerCreateOrUpdateProject = ({
  idClient,
  modalStatus,
  handleCreateLocation,
  handleCreateNewProjectStatus,
  handleCreateSource,
  handleChangeModalStatus,
  onCancel,
  onSubmit,
}: Props) => {
  const { useBreakpoint } = Grid;
  const { xs, md } = useBreakpoint();
  const [form] = Form.useForm();
  const [values, setValues] = useState<ProjectProps>(PROJECT_INITIAL_STATE);
  const [locationName, setLocationName] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [projectStatusName, setProjectStatusName] = useState("");
  const inputRef = useRef<InputRef>(null);
  const inputRefStatus = useRef<InputRef>(null);
  const inputRefSource = useRef<InputRef>(null);
  const {
    usersList,
    userOptions,
    projectTemplateList,
    customerByClient,
    statusByTemplate,
    openItemModal,
    handleEditData,
    handleCreateData,
    handleDeleteData,
    getData,
    getDataById,
  } = useContext(DataContext);
  const { clientInformation } = useContext(AuthContext);
  const [isCustomerDrawerOpen, setIsCustomerDrawerOpen] = useState(false);
  const [statusOptions, setStatusOptions] = useState<
    { label: string; value: string }[]
  >([]);

  useEffect(() => {
    if (values) {
      form.setFieldsValue(values);
    } else {
      form.setFieldsValue(PROJECT_INITIAL_STATE);
      setValues(PROJECT_INITIAL_STATE);
    }
  }, [values]);

  useEffect(() => {
    values._id &&
      values._id !== "" &&
      getData("tasks", idClient, undefined, {
        project: values._id,
        active: true,
      });
    values._id &&
      values._id !== "" &&
      getData("activities-paginated", idClient, undefined, {
        project: values._id,
        active: true,
      });
  }, [values._id]);

  useEffect(() => {
    if (modalStatus?.payload) {
      setValues(modalStatus.payload);
    } else {
      setValues(PROJECT_INITIAL_STATE);
    }
  }, [modalStatus]);

  useEffect(() => {
    if (values.projectTemplate) {
      getDataById({
        type: "template-status",
        endpoint: `project-template/${idClient}/${values.projectTemplate?._id}`,
      });
    } else {
      let options =
        clientInformation?.projectStatus?.map((status) => ({
          label: status.name as string,
          value: status._id as string,
        })) || [];
      setStatusOptions(options);
    }
  }, [values.projectTemplate, clientInformation]);

  const createLocation = async () => {
    if (!locationName.trim()) {
      message.error("Please enter a location name.");
      return;
    }
    try {
      handleCreateLocation(locationName);
      setLocationName("");
    } catch (error) {
      message.error("Failed to create. Please try again.");
    }
  };
  const createSource = async () => {
    if (!sourceName.trim()) {
      message.error("Please enter a Source name.");
      return;
    }
    try {
      handleCreateSource(sourceName);
      setSourceName("");
    } catch (error) {
      message.error("Failed to create. Please try again.");
    }
  };
  const createProjectStatus = async () => {
    if (!projectStatusName.trim()) {
      message.error("Please enter an status name.");
      return;
    }
    try {
      if (!values.projectTemplate) {
        handleCreateNewProjectStatus(projectStatusName);
      } else {
        handleEditData({
          endpoint: `project-template/${idClient}/${values.projectTemplate?._id}`,
          newData: {
            newProjectStatus: projectStatusName,
          },
          next: () => {
            getDataById({
              type: "template-status",
              endpoint: `project-template/${idClient}/${values.projectTemplate?._id}`,
            });
          },
          showAlert: false,
        });
      }
      setProjectStatusName("");
    } catch (error) {
      message.error("Failed to create. Please try again.");
    }
  };

  const saveProjectPartial = () => {
    if (clientInformation?._id && values.customers?.length > 0) {
      modalStatus?.create
        ? handleCreateData({
            endpoint: "projects",
            idClient: clientInformation._id,
            data: values,
            next: (response) => {
              setValues(response);
              handleChangeModalStatus(response);
              getData("projects", clientInformation._id, undefined, {
                active: true,
              });
            },
            showAlert: false,
          })
        : handleEditData({
            endpoint: `project/${clientInformation._id}/${values._id}`,
            newData: values,
            next: () => {
              getData("projects", clientInformation._id, undefined, {
                active: true,
              });
            },
            showAlert: false,
          });
    }
  };

  return (
    <>
      <Drawer
        title="Project"
        open={modalStatus?.open}
        zIndex={999}
        onClose={() => {
          onCancel();
        }}
        footer={
          <>
            <Button type="default" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              style={{ marginLeft: 8 }}
              onClick={() => {
                form
                  .validateFields()
                  .then(() => {
                    onSubmit(values);
                    form.setFieldsValue(PROJECT_INITIAL_STATE);
                    setValues(PROJECT_INITIAL_STATE);
                  })
                  .catch((error) => {
                    console.error("Validation failed:", error);
                  });
              }}
            >
              Save Project
            </Button>
          </>
        }
        extra={
          <>
            <Button type="default" onClick={onCancel}>
              Cancel
            </Button>
            <Button
              type="primary"
              style={{ marginLeft: 8 }}
              onClick={() => {
                form
                  .validateFields()
                  .then(() => {
                    onSubmit(values);
                    form.setFieldsValue(PROJECT_INITIAL_STATE);
                    setValues(PROJECT_INITIAL_STATE);
                  })
                  .catch((error) => {
                    console.error("Validation failed:", error);
                  });
              }}
            >
              Save Project
            </Button>
          </>
        }
        size="large"
        width={xs || md ? "100vw" : "80vw"}
      >
        <Row gutter={16}>
          <Form layout="vertical" form={form} initialValues={values}>
            <Row gutter={16}>
              {/* Project Template */}
              <Col lg={12} md={12} sm={24} xs={24}>
                <Card
                  hoverable
                  size="small"
                  title={
                    <>
                      Project Information
                      <Divider type="vertical" />
                    </>
                  }
                  extra={
                    <Button
                      type="primary"
                      size={xs ? "small" : "middle"}
                      disabled={
                        !values.name ||
                        !values.active ||
                        (modalStatus?.selected &&
                          modalStatus?.selected?.length > 0)
                      }
                      icon={
                        isCustomerDrawerOpen ? (
                          <UsergroupAddOutlined />
                        ) : (
                          <UsergroupAddOutlined />
                        )
                      }
                      onClick={() =>
                        setIsCustomerDrawerOpen(!isCustomerDrawerOpen)
                      }
                      style={{ margin: 5 }}
                    >
                      {modalStatus?.payload?._id
                        ? "Edit Customer List"
                        : "Add Customers"}
                    </Button>
                  }
                >
                  <Row gutter={[16, 16]}>
                    <Col lg={8} md={12} sm={12} xs={12}>
                      <Form.Item label="Template">
                        <Select
                          showSearch
                          allowClear
                          filterOption={filterOption}
                          value={values.projectTemplate?._id}
                          placeholder="Select Template"
                          style={{ width: "100%" }}
                          options={projectTemplateList.map(
                            (projectTemplate) => ({
                              label: projectTemplate.name,
                              value: projectTemplate._id,
                            }),
                          )}
                          onChange={(value) => {
                            if (!value) {
                              setValues((prev) => ({
                                ...prev,
                                projectTemplate: null,
                                status: null,
                              }));
                              return;
                            }
                            const selected = projectTemplateList.find(
                              (projectTemplate) =>
                                projectTemplate._id === value,
                            );
                            setValues((prev) => ({
                              ...prev,
                              projectTemplate: selected as ProjectTemplateProps,
                              status: null,
                              startDate: !prev.startDate
                                ? selected?.startDate || null
                                : prev.startDate,
                              endDate: !prev.endDate
                                ? selected?.endDate || null
                                : prev.endDate,
                              description: !prev.description
                                ? selected?.description || ""
                                : prev.description,
                            }));
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={8} md={12} sm={12} xs={12}>
                      {modalStatus?.selected &&
                      modalStatus?.selected?.length > 0 ? (
                        <Form.Item label="Name" name="name">
                          <Input
                            placeholder="Enter project name"
                            value={values.name}
                            disabled
                          />
                        </Form.Item>
                      ) : (
                        <Form.Item
                          label="Name"
                          name="name"
                          rules={[
                            {
                              required: true,
                              message: "Project name is required",
                            },
                          ]}
                        >
                          <Input
                            placeholder="Enter project name"
                            value={values.name}
                            onChange={(event) =>
                              setValues((prev) => ({
                                ...prev,
                                name: event.target.value,
                              }))
                            }
                            onBlur={() =>
                              modalStatus?.create &&
                              setIsCustomerDrawerOpen(true)
                            }
                          />
                        </Form.Item>
                      )}
                    </Col>
                    <Col lg={8} md={12} sm={12} xs={12}>
                      <Form.Item label="Active" name="active">
                        <Select
                          value={values.active ? "yes" : "no"}
                          onChange={(value) =>
                            setValues((prev) => ({
                              ...prev,
                              active: value === "yes",
                            }))
                          }
                          disabled={modalStatus?.create}
                          options={[
                            { label: "Yes", value: true },
                            { label: "No", value: false },
                          ]}
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={8} md={12} sm={12} xs={12}>
                      <Form.Item label="Status">
                        <Select
                          filterOption={filterOptionGroup}
                          showSearch
                          placeholder="Select Status"
                          value={values.status?._id}
                          onChange={(newValue) => {
                            let selected: ProjectTemplateProps | null = null;
                            if (!values.projectTemplate?._id) {
                              selected = clientInformation?.projectStatus?.find(
                                (status) => status._id === newValue,
                              ) as any;
                            } else {
                              selected = statusByTemplate?.find(
                                (status) => status._id === newValue,
                              ) as any;
                            }
                            setValues((prev) => ({
                              ...prev,
                              status: selected as any,
                            }));
                          }}
                          dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                <Input
                                  placeholder="New Status"
                                  ref={inputRefStatus}
                                  value={projectStatusName}
                                  onChange={(event) =>
                                    setProjectStatusName(event.target.value)
                                  }
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                                <Tooltip title="Add New Status">
                                  <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={createProjectStatus}
                                  ></Button>
                                </Tooltip>
                              </Space>
                            </>
                          )}
                        >
                          <Select.OptGroup label="Template Status">
                            {statusByTemplate.map((s) => (
                              <Select.Option key={s._id} value={s._id}>
                                {s.name}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                          <Select.OptGroup label="General Status">
                            {statusOptions.map((s) => (
                              <Select.Option key={s.value} value={s.value}>
                                {s.label}
                              </Select.Option>
                            ))}
                          </Select.OptGroup>
                        </Select>
                      </Form.Item>
                    </Col>
                    <Col lg={8} md={12} sm={12} xs={12}>
                      <Form.Item label="Tags">
                        <TagSelector
                          selectedTags={values.tags || []}
                          setSelectedTags={(selectedTags) => {
                            setValues({ ...values, tags: selectedTags });
                          }}
                        />
                      </Form.Item>
                    </Col>
                    <Col lg={8} md={12} sm={12} xs={12}>
                      <Form.Item label="Assigned To">
                        <Select
                          maxTagCount="responsive"
                          allowClear={true}
                          mode="multiple"
                          showSearch
                          filterOption={filterOption}
                          value={
                            Array.isArray(values.assignedTo)
                              ? values.assignedTo.map((user) => user._id)
                              : values.assignedTo?._id
                                ? [values.assignedTo?._id]
                                : null
                          }
                          placeholder="Select user"
                          style={{ width: "100%" }}
                          options={userOptions}
                          onChange={(value) => {
                            if (!value) {
                              setValues((prev) => ({
                                ...prev,
                                assignedTo: null,
                              }));
                              return;
                            }
                            const selected = usersList.filter((user) =>
                              value.includes(user._id as any),
                            );
                            setValues({ ...values, assignedTo: selected });
                          }}
                        />
                      </Form.Item>
                    </Col>

                    <Col lg={12} md={12} sm={12} xs={12}>
                      <Form.Item label="Location">
                        <Select
                          placeholder="Select Location"
                          filterOption={filterOption}
                          showSearch
                          options={clientInformation?.locations?.map(
                            (location) => ({
                              label: location.name,
                              value: location._id,
                            }),
                          )}
                          value={values.location?._id}
                          onChange={(newValue) => {
                            let selected = clientInformation?.locations?.find(
                              (location) => location._id === newValue,
                            );
                            setValues((prev) => ({
                              ...prev,
                              location: selected as any,
                            }));
                          }}
                          dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                <Input
                                  placeholder="New location"
                                  ref={inputRef}
                                  value={locationName}
                                  onChange={(event) =>
                                    setLocationName(event.target.value)
                                  }
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                                <Tooltip title="Add New Location">
                                  <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={createLocation}
                                  ></Button>
                                </Tooltip>
                              </Space>
                            </>
                          )}
                        ></Select>
                      </Form.Item>
                    </Col>
                    <Col lg={12} md={12} sm={12} xs={12}>
                      <Form.Item label="Source">
                        <Select
                          filterOption={filterOption}
                          showSearch
                          placeholder="Select Source"
                          options={clientInformation?.sources?.map(
                            (source) => ({
                              label: source.name,
                              value: source._id,
                            }),
                          )}
                          value={values.source?._id}
                          onChange={(newValue) => {
                            let selected = clientInformation?.sources?.find(
                              (source) => source._id === newValue,
                            );
                            setValues((prev) => ({
                              ...prev,
                              source: selected as any,
                            }));
                          }}
                          dropdownRender={(menu) => (
                            <>
                              {menu}
                              <Divider style={{ margin: "8px 0" }} />
                              <Space style={{ padding: "0 8px 4px" }}>
                                <Input
                                  placeholder="New Source"
                                  ref={inputRefSource}
                                  value={sourceName}
                                  onChange={(event) =>
                                    setSourceName(event.target.value)
                                  }
                                  onKeyDown={(e) => e.stopPropagation()}
                                />
                                <Tooltip title="Add New Source">
                                  <Button
                                    type="text"
                                    icon={<PlusOutlined />}
                                    onClick={createSource}
                                  ></Button>
                                </Tooltip>
                              </Space>
                            </>
                          )}
                        ></Select>
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
              </Col>

              <Col lg={12} md={12} sm={24} xs={24}>
                <Card title="Project Timing" size="small" hoverable>
                  <Row gutter={16}>
                    {/* Start Date */}
                    <Col lg={12} md={12} sm={12} xs={12}>
                      <Form.Item label="Start Date">
                        <DatePicker
                          format="MM/DD/YYYY"
                          style={{ width: "100%" }}
                          value={values.startDate && dayjs(values.startDate)}
                          onChange={(date) =>
                            setValues((prev) => ({
                              ...prev,
                              startDate: date as any,
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>
                    {/* End Date */}
                    <Col lg={12} md={6} sm={12} xs={12}>
                      <Form.Item label="End Date">
                        <DatePicker
                          format="MM/DD/YYYY"
                          style={{ width: "100%" }}
                          value={values.endDate && dayjs(values.endDate)}
                          onChange={(date) =>
                            setValues((prev) => ({
                              ...prev,
                              endDate: date as any,
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>
                    <Col
                      lg={6}
                      md={6}
                      sm={12}
                      xs={12}
                      style={{ alignContent: "center" }}
                    >
                      <Form.Item>
                        <Checkbox
                          checked={values.recurring?.isRecurring}
                          onChange={(e) =>
                            setValues((prev) => ({
                              ...prev,
                              recurring: {
                                isRecurring: e.target.checked,
                                frequency: "monthly",
                                startFrom: dayjs(),
                              },
                            }))
                          }
                        >
                          Make this Project recurring
                        </Checkbox>
                      </Form.Item>
                    </Col>
                    <Col lg={6} md={6} sm={12} xs={12}>
                      <Form.Item
                        name={["recurring", "frequency"]}
                        label="Frequency"
                      >
                        <Select
                          placeholder="Repeat every..."
                          disabled={!values.recurring?.isRecurring}
                          value={
                            values.recurring && values.recurring?.frequency
                          }
                          filterOption={filterOption}
                          showSearch
                          onChange={(value) =>
                            setValues((prev) => ({
                              ...prev,
                              recurring: {
                                isRecurring:
                                  prev.recurring?.isRecurring || true,
                                frequency: value,
                                startFrom: prev.recurring?.startFrom || dayjs(),
                              },
                            }))
                          }
                          options={[
                            {
                              label: "Daily",
                              value: "daily",
                            },
                            {
                              label: "Weekly",
                              value: "weekly",
                            },
                            {
                              label: "Monthly",
                              value: "monthly",
                            },
                            {
                              label: "Bi-Monthly",
                              value: "bi-monthly",
                            },
                            {
                              label: "Quarterly",
                              value: "quarterly",
                            },
                            {
                              label: "Yearly",
                              value: "yearly",
                            },
                          ]}
                        ></Select>
                      </Form.Item>
                    </Col>
                    <Col lg={6} md={6} sm={12} xs={12}>
                      <Form.Item
                        label="Start From"
                        rules={[
                          {
                            required: true,
                            message: "Please pick a start date",
                          },
                        ]}
                      >
                        <DatePicker
                          value={
                            values.recurring && values.recurring?.startFrom
                              ? dayjs(values.recurring?.startFrom)
                              : null
                          }
                          style={{ width: "100%" }}
                          format={"MM/DD/YYYY"}
                          onChange={(value) =>
                            setValues((prev) => ({
                              ...prev,
                              recurring: {
                                isRecurring:
                                  prev.recurring?.isRecurring || true,
                                frequency:
                                  prev.recurring?.frequency || "monthly",
                                startFrom: value,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>

                    <Col lg={6} md={6} sm={12} xs={12}>
                      <Form.Item label="End At (optional)">
                        <DatePicker
                          value={
                            values.recurring && values.recurring?.endAt
                              ? dayjs(values.recurring?.endAt)
                              : null
                          }
                          style={{ width: "100%" }}
                          format={"MM/DD/YYYY"}
                          onChange={(value) =>
                            setValues((prev) => ({
                              ...prev,
                              recurring: {
                                isRecurring:
                                  prev.recurring?.isRecurring || true,
                                frequency:
                                  prev.recurring?.frequency || "monthly",
                                startFrom: prev.recurring?.startFrom || dayjs(),
                                endAt: value,
                              },
                            }))
                          }
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                </Card>
                {/* Description */}

                <Col lg={24} md={24} sm={24} xs={24} style={{ marginTop: 16 }}>
                  <Form.Item label="Description" name="description">
                    <Input.TextArea
                      rows={6}
                      placeholder="Enter project description"
                      value={values.description}
                      onChange={(event) =>
                        setValues((prev) => ({
                          ...prev,
                          description: event.target.value,
                        }))
                      }
                    />
                  </Form.Item>
                </Col>
              </Col>
              {values._id && (
                <>
                  <Col lg={12} md={24} sm={24} xs={24}>
                    <TaskList
                      origin="project"
                      onAddNewTask={() =>
                        openItemModal("task", true, {
                          ...TASK_INITIAL_STATE,
                          project: values._id,
                          customers: values.customers,
                          assignedTo: values.assignedTo,
                          origin: "project",
                        })
                      }
                      onEditTask={(task) =>
                        openItemModal("task", false, {
                          ...task,
                          origin: "project",
                        })
                      }
                      onDuplicateTask={(task) =>
                        openItemModal("task", true, {
                          ...task,
                          _id: "",
                          description: task.description + " (Copy)",
                          origin: "project",
                        })
                      }
                      onChangePage={(current, pageSize) => {
                        getData("tasks", idClient, undefined, {
                          project: values._id,
                          active: true,
                          page: current,
                          perPage: pageSize,
                        });
                      }}
                      onDeleteTask={(task, _, pageSize, currentPage) =>
                        handleDeleteData({
                          endpoint: `task/${idClient}/${task._id}`,
                          next: () => {
                            getData("tasks", idClient, undefined, {
                              project: values._id,
                              active: true,
                              page: currentPage,
                              perPage: pageSize,
                            });
                          },
                        })
                      }
                    />
                  </Col>
                  <Col lg={12} md={24} sm={24} xs={24}>
                    <ActivityList
                      origin="project"
                      onAddNewActivity={() =>
                        openItemModal("activity", true, {
                          ...ACTIVITY_INITIAL_STATE,
                          project: values._id,
                          customers: values.customers,
                          assignedTo: values.assignedTo,
                          origin: "project",
                        })
                      }
                      onEditActivity={(activity) =>
                        openItemModal("activity", false, {
                          ...activity,
                          customers: values.customers,
                          origin: "project",
                        })
                      }
                      onDuplicateActivity={(activity) =>
                        openItemModal("activity", true, {
                          ...activity,
                          _id: "",
                          description: activity.description + " (Copy)",
                          origin: "project",
                        })
                      }
                      onChangePage={(current, pageSize) => {
                        getData("activities-paginated", idClient, undefined, {
                          project: values._id,
                          active: true,
                          page: current,
                          perPage: pageSize,
                        });
                      }}
                      onDeleteActivity={(activity, pageSize, currentPage) =>
                        handleDeleteData({
                          endpoint: `activity/${idClient}/${activity._id}`,
                          next: () => {
                            getData(
                              "activities-paginated",
                              idClient,
                              undefined,
                              {
                                project: values._id,
                                active: true,
                                page: currentPage,
                                perPage: pageSize,
                              },
                            );
                          },
                        })
                      }
                    />
                  </Col>
                </>
              )}
            </Row>

            {/* Dynamic Custom Fields */}
            <Form.List name="customFields">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Row gutter={16} key={key} align="middle">
                      {/* Field Name */}
                      <Col lg={6} md={6} sm={12} xs={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "name"]}
                          rules={[
                            {
                              required: true,
                              message: "Field name is required",
                            },
                          ]}
                        >
                          <Input placeholder="Field Name" />
                        </Form.Item>
                      </Col>

                      {/* Field Type */}
                      <Col lg={6} md={6} sm={12} xs={12}>
                        <Form.Item
                          {...restField}
                          name={[name, "type"]}
                          rules={[{ required: true, message: "Select a type" }]}
                        >
                          <Select
                            filterOption={filterOption}
                            showSearch
                            placeholder="Field Type"
                            options={fieldTypes}
                          ></Select>
                        </Form.Item>
                      </Col>

                      {/* Field Value (Dynamic Based on Type) */}
                      <Col lg={8} md={12} sm={12} xs={12}>
                        <Form.Item shouldUpdate>
                          {({ getFieldValue }) => {
                            const fieldType = getFieldValue([
                              "customFields",
                              name,
                              "type",
                            ]);

                            if (fieldType === "String") {
                              return (
                                <Form.Item
                                  {...restField}
                                  name={[name, "value"]}
                                >
                                  <Input placeholder="Enter text" />
                                </Form.Item>
                              );
                            }

                            if (fieldType === "Boolean") {
                              return (
                                <Form.Item
                                  {...restField}
                                  name={[name, "value"]}
                                  valuePropName="checked"
                                >
                                  <Switch />
                                </Form.Item>
                              );
                            }

                            if (fieldType === "Date") {
                              return (
                                <Form.Item
                                  {...restField}
                                  name={[name, "value"]}
                                >
                                  <DatePicker style={{ width: "100%" }} />
                                </Form.Item>
                              );
                            }

                            if (fieldType === "Number") {
                              return (
                                <Form.Item
                                  {...restField}
                                  name={[name, "value"]}
                                >
                                  <InputNumber style={{ width: "100%" }} />
                                </Form.Item>
                              );
                            }

                            return null;
                          }}
                        </Form.Item>
                      </Col>

                      {/* Remove Field Button */}
                      <Col lg={4} md={6} sm={12} xs={12}>
                        <Button type="link" danger onClick={() => remove(name)}>
                          Remove
                        </Button>
                      </Col>
                    </Row>
                  ))}

                  {/* Add Custom Field Button */}
                  {/* <Form.Item>
                  <Button type="dashed" onClick={() => add()} block>
                    + Add Custom Field
                  </Button>
                </Form.Item> */}
                </>
              )}
            </Form.List>
          </Form>

          <Drawer
            title={
              modalStatus?.payload?._id ? "Edit Customer List" : "Add Customers"
            }
            placement="right"
            open={isCustomerDrawerOpen}
            onClose={() => {
              if (values.customers.length > 0) {
                Modal.confirm({
                  title: "Save Customer List?",
                  content:
                    "You have selected customers. Do you want to save the customer list before closing?",
                  okText: "Save",
                  cancelText: "Don't Save",
                  onOk: () => {
                    saveProjectPartial();
                    setIsCustomerDrawerOpen(false);
                  },
                  onCancel: () => {
                    setIsCustomerDrawerOpen(false);
                  },
                });
              } else {
                setIsCustomerDrawerOpen(false);
              }
            }}
            maskClosable={true}
            width={500}
            zIndex={1001}
            extra={
              <Tooltip title="Save Customer List">
                <Button
                  disabled={values.customers.length > 0 ? false : true}
                  type="primary"
                  icon={<UsergroupAddOutlined />}
                  onClick={() => {
                    saveProjectPartial();
                    setIsCustomerDrawerOpen(false);
                  }}
                >
                  Save
                </Button>
              </Tooltip>
            }
          >
            <CustomerListWithNoDetails
              customersSelected={
                values.customers.map((customer) => customer._id) as string[]
              }
              setCustomerList={(customersSelected: any) => {
                let customers = customerByClient.filter((customer) =>
                  customersSelected.includes(customer._id),
                );
                setValues((prev) => ({
                  ...prev,
                  customers: customers,
                }));
              }}
            />
          </Drawer>
        </Row>
      </Drawer>
    </>
  );
};
